import { signal, useSignalValue } from "../../../utils/signal";
import { calcBetEV } from "../parlay-builder/parlay-engine";
import { ParlayLeg } from "../parlay-builder/parlay-share-utils";

/**
 * Client store for the on-the-fly EV parlay slips. Boards add legs via
 * {@link toggleSlipLeg}; the app-root {@link ParlaySlipDock} renders the
 * slips and their combined math. Persisted to localStorage so a slip in
 * progress survives navigation between boards and reloads.
 *
 * A parlay lives at ONE sportsbook, so there is one slip PER BOOK:
 * legs are stored flat but grouped by {@link normBook}, and adding a
 * leg routes it to (and activates) its book's slip. Legs are
 * {@link ParlayLeg} — the same shape the parlay builder's engine
 * computes with, so combined odds/EV/Kelly math is shared, not
 * re-derived.
 */

const STORAGE_KEY = "bb_parlay_slip_v1";

/** parlay-engine's MAX_LEGS is 7 for enumeration; hand-built slips get a
 *  little more rope but still a hard cap (books rarely take 10+). */
export const MAX_SLIP_LEGS = 10;

/** Grouping key for a book — boards vary casing on bookie strings. */
export const normBook = (bookie: string): string => bookie.trim().toLowerCase();

/** Identity of a leg: one (book, event, market, pick) tuple — the same
 *  pick at two books belongs to two different slips. */
export const slipLegKey = (leg: {
  bookie: string;
  event: string;
  market: string;
  pick: string;
}): string =>
  `${normBook(leg.bookie)}||${leg.event}||${leg.market}||${leg.pick}`.toLowerCase();

/** The pick at any book: the same outcome sits in at most one slip. */
export const slipPickKey = (leg: { event: string; market: string; pick: string }): string =>
  `${leg.event}||${leg.market}||${leg.pick}`.toLowerCase();

/** The leg priced at `bookKey`, or null when that book doesn't quote it. */
export function requoteLeg(leg: ParlayLeg, bookKey: string): ParlayLeg | null {
  if (normBook(leg.bookie) === bookKey) return leg;
  const alt = (leg.altBooks ?? []).find((a) => normBook(a.bookie) === bookKey);
  if (!alt) return null;
  return {
    ...leg,
    bookie: alt.bookie,
    odds: alt.odds,
    evPercent: Math.round(calcBetEV(alt.odds, leg.fairOdds) * 10) / 10,
  };
}

function isLeg(value: unknown): value is ParlayLeg {
  const leg = value as ParlayLeg;
  return (
    !!leg &&
    typeof leg.event === "string" &&
    typeof leg.market === "string" &&
    typeof leg.pick === "string" &&
    Number.isFinite(leg.odds) &&
    Number.isFinite(leg.fairOdds) &&
    typeof leg.bookie === "string"
  );
}

function load(): ParlayLeg[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Cap is per-book (one slip per book), not on the flat list.
    const perBook = new Map<string, number>();
    const out: ParlayLeg[] = [];
    for (const leg of parsed.filter(isLeg)) {
      const book = normBook(leg.bookie);
      const count = perBook.get(book) ?? 0;
      if (count >= MAX_SLIP_LEGS) continue;
      perBook.set(book, count + 1);
      out.push(leg);
    }
    return out;
  } catch {
    return [];
  }
}

function persist(legs: ParlayLeg[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(legs));
  } catch {
    /* storage full/blocked — slip still works for the session */
  }
}

export const parlaySlipSignal = signal<ParlayLeg[]>(load());

/** Dock expanded/collapsed — global so add-buttons could pop it later. */
export const parlaySlipOpenSignal = signal<boolean>(false);

/** Which book's slip the dock is showing (normalized key). Falls back
 *  to the first book holding legs when stale. */
export const activeSlipBookSignal = signal<string | null>(null);

function setLegs(legs: ParlayLeg[]): void {
  parlaySlipSignal.value = legs;
  persist(legs);
}

export type ToggleSlipResult = "added" | "removed" | "full";

/** Add the leg to its book's slip (activating that slip), or remove it
 *  if it's already there. */
export function toggleSlipLeg(leg: ParlayLeg): ToggleSlipResult {
  const legs = parlaySlipSignal.peek();
  const key = slipLegKey(leg);
  const without = legs.filter((l) => slipLegKey(l) !== key);
  if (without.length < legs.length) {
    setLegs(without);
    return "removed";
  }
  const book = normBook(leg.bookie);
  if (legs.filter((l) => normBook(l.bookie) === book).length >= MAX_SLIP_LEGS) {
    return "full";
  }
  setLegs([...legs, leg]);
  activeSlipBookSignal.value = book;
  return "added";
}

export function removeSlipLeg(key: string): void {
  setLegs(parlaySlipSignal.peek().filter((l) => slipLegKey(l) !== key));
}

/** Empty one book's slip (after tracking it, or Clear). */
export function clearBookSlip(bookie: string): void {
  const book = normBook(bookie);
  setLegs(parlaySlipSignal.peek().filter((l) => normBook(l.bookie) !== book));
}

/**
 * Re-quote: replace one book's slip with the same picks priced at
 * another book (the dock computes the re-priced legs from each leg's
 * altBooks snapshot). Merges into the target book's slip, deduping on
 * leg identity. Returns false when the merged slip would bust the cap.
 */
export function replaceBookSlip(fromBookie: string, newLegs: ParlayLeg[]): boolean {
  if (newLegs.length === 0) return false;
  const from = normBook(fromBookie);
  const to = normBook(newLegs[0].bookie);
  const rest = parlaySlipSignal
    .peek()
    .filter((l) => normBook(l.bookie) !== from);
  const newKeys = new Set(newLegs.map(slipLegKey));
  const kept = rest.filter((l) => !newKeys.has(slipLegKey(l)));
  if (
    kept.filter((l) => normBook(l.bookie) === to).length + newLegs.length >
    MAX_SLIP_LEGS
  ) {
    return false;
  }
  setLegs([...kept, ...newLegs]);
  activeSlipBookSignal.value = to;
  return true;
}

export function clearSlip(): void {
  setLegs([]);
  parlaySlipOpenSignal.value = false;
}

/**
 * Merge legs (all one book by construction — a copied parlay) back into
 * that book's slip, activate it, and pop the dock open. Returns how many
 * legs were actually added (0 = already all there, or the slip is full).
 */
export function copyLegsToSlip(legsToAdd: ParlayLeg[]): number {
  if (legsToAdd.length === 0) return 0;
  const current = parlaySlipSignal.peek();
  const existing = new Set(current.map(slipLegKey));
  const book = normBook(legsToAdd[0].bookie);
  let inBook = current.filter((l) => normBook(l.bookie) === book).length;
  const fresh: ParlayLeg[] = [];
  for (const leg of legsToAdd) {
    if (existing.has(slipLegKey(leg))) continue;
    if (inBook >= MAX_SLIP_LEGS) break;
    fresh.push(leg);
    inBook += 1;
  }
  if (fresh.length > 0) setLegs([...current, ...fresh]);
  activeSlipBookSignal.value = book;
  parlaySlipOpenSignal.value = true;
  return fresh.length;
}

// ── Tracked-parlay snapshots (per-device) ────────────────────────────
//
// The backend flattens a tracked parlay into one bet row, so the legs
// only exist client-side. Tracking from the dock saves them here keyed
// by the created bet id; My Bets offers "Copy to slip" for un-started
// parlays that have a snapshot on this device.

const SNAPSHOT_KEY = "bb_parlay_snapshots_v1";
const MAX_SNAPSHOTS = 40;

type SnapshotMap = Record<string, { legs: ParlayLeg[]; at: number }>;

let snapshotCache: SnapshotMap | null = null;

function readSnapshots(): SnapshotMap {
  if (snapshotCache) return snapshotCache;
  try {
    const parsed = JSON.parse(localStorage.getItem(SNAPSHOT_KEY) ?? "{}");
    snapshotCache =
      parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as SnapshotMap)
        : {};
  } catch {
    snapshotCache = {};
  }
  return snapshotCache;
}

function writeSnapshots(map: SnapshotMap): void {
  snapshotCache = map;
  try {
    localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(map));
  } catch {
    /* storage full/blocked — copies just won't survive this session */
  }
}

export function saveParlaySnapshot(betId: number, legs: ParlayLeg[]): void {
  const map = { ...readSnapshots(), [String(betId)]: { legs, at: Date.now() } };
  const ids = Object.keys(map).sort((a, b) => map[b].at - map[a].at);
  for (const id of ids.slice(MAX_SNAPSHOTS)) delete map[id];
  writeSnapshots(map);
}

export function getParlaySnapshot(betId: number): ParlayLeg[] | null {
  const entry = readSnapshots()[String(betId)];
  if (!entry || !Array.isArray(entry.legs)) return null;
  const legs = entry.legs.filter(isLeg);
  return legs.length >= 2 ? legs : null;
}

/** Drop a snapshot once its bet is removed from the tracker. */
export function deleteParlaySnapshot(betId: number): void {
  const map = { ...readSnapshots() };
  if (map[String(betId)] == null) return;
  delete map[String(betId)];
  writeSnapshots(map);
}

export function useSlipLegs(): ParlayLeg[] {
  return useSignalValue(parlaySlipSignal);
}

/** The slip leg holding this pick at whichever book it went to. */
export function useSlipPick(leg: ParlayLeg | null | undefined): ParlayLeg | undefined {
  const legs = useSignalValue(parlaySlipSignal);
  if (!leg) return undefined;
  const key = slipPickKey(leg);
  return legs.find((l) => slipPickKey(l) === key);
}

/** Reactive "is this leg on the slip" for board button state. */
export function useInSlip(key: string): boolean {
  const legs = useSignalValue(parlaySlipSignal);
  return legs.some((l) => slipLegKey(l) === key);
}
